/**
 * Rich Text Utilities für sauberes HTML-Rendering mit Font-Inheritance
 */
import React from 'react';

/**
 * Bereinigt HTML-Content für saubere Darstellung
 * @param {string} htmlContent - HTML-String vom Rich Text Editor
 * @returns {string} - Bereinigter HTML-String
 */
export const cleanRichTextHTML = (htmlContent) => {
    if (!htmlContent) return '';
    
    let cleaned = htmlContent;
    
    // ✅ SCHRITT 1: HTML-Entities dekodieren (für Live-Seiten Kompatibilität)
    // EINFACHER FIX: Verwende Browser-native HTML-Dekodierung
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleaned;
    cleaned = tempDiv.innerHTML;
    
    // Fallback für spezielle Entities
    cleaned = cleaned
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ');
    
    // ✅ SCHRITT 2: CSS-Variablen in Style-Attributen korrigieren
    // rgba(39,34,32,var(--O42jJQ,1)) → rgba(39,34,32,1)
    cleaned = cleaned.replace(
        /rgba\(([^,]+),([^,]+),([^,]+),var\([^,]+,([^)]+)\)\)/g,
        'rgba($1,$2,$3,$4)'
    );
    
    // ✅ SCHRITT 3: Entferne ALLE P-Tags (auch mit Attributen) und ersetze durch BR-Tags
    cleaned = cleaned
        // Entferne leere P-Tags (mit und ohne Attribute)
        .replace(/<p[^>]*><\/p>/g, '<br>')
        .replace(/<p[^>]*><br[^>]*><\/p>/g, '<br>')
        // ✅ WICHTIG: Entferne ALLE P-Tags, auch die mit Style-Attributen
        .replace(/<p[^>]*>/g, '') // Öffnende P-Tags mit beliebigen Attributen
        .replace(/<\/p>/g, '<br>') // Schließende P-Tags zu BR
        // Entferne doppelte BR-Tags
        .replace(/<br\s*\/?><br\s*\/?>/g, '<br>')
        // Entferne BR am Anfang und Ende
        .replace(/^<br\s*\/?>/, '')
        .replace(/<br\s*\/?>$/, '')
        // Entferne leere Spans und Divs (auch mit Attributen)
        .replace(/<span[^>]*><\/span>/g, '')
        .replace(/<div[^>]*><\/div>/g, '');
    
    // ✅ SCHRITT 4: BEHALTE HTML-Tags inkl. Color-Spans
    // Bereinige nur Tags ohne wichtige Styles
    cleaned = cleaned
        .replace(/<strong[^>]*>/g, '<strong>') // Bereinige Strong-Tags aber behalte sie
        .replace(/<b[^>]*>/g, '<b>') // Bereinige B-Tags aber behalte sie
        .replace(/<em[^>]*>/g, '<em>') // Bereinige Em-Tags aber behalte sie
        .replace(/<i[^>]*>/g, '<i>') // Bereinige I-Tags aber behalte sie
        .replace(/<u[^>]*>/g, '<u>') // Bereinige U-Tags aber behalte sie
        
    // ✅ SCHRITT 5: Bereinige Style-Attribute in Spans ABER BEHALTE color
        .replace(/<span\s+style="([^"]+)"/gi, (match, styleContent) => {
            // Extrahiere nur color aus dem style
            const colorMatch = styleContent.match(/color:\s*([^;]+)/i);
            if (colorMatch) {
                return `<span style="color: ${colorMatch[1]}"`;
            }
            return '<span'; // Entferne Style wenn kein color
        })
        // Entferne Spans ohne Attribute (leere Spans)
        .replace(/<span>/g, '')
        .replace(/<span\s+>/g, '')
        // Entferne leere Style-Attribute
        .replace(/style=""\s*/g, '');
    
    return cleaned;
};

/**
 * ✅ NEUE SAFE RICH TEXT RENDERER - Ohne dangerouslySetInnerHTML
 * Rendert Rich Text mit korrekter Font-Inheritance und Bold-Support
 * @param {string} content - Rich Text Content
 * @param {object} style - Style-Objekt für das Container-Element
 * @param {string} className - CSS-Klasse für das Container-Element
 * @returns {React.Element} - React-Element
 */
export const renderSafeRichText = (content, style = {}, className = 'rich-text-output') => {
    if (!content) return null;
    
    // ✅ PARSE HTML zu React-Elementen
    const parseHtmlToReact = (htmlString) => {
        if (!htmlString) return '';
        
        // Teile Text an Bold-Tags auf
        const parts = htmlString.split(/(<\/?(?:strong|b)>)/i);
        let isBold = false;
        const elements = [];
        
        parts.forEach((part, index) => {
            if (part.match(/<(?:strong|b)>/i)) {
                isBold = true;
            } else if (part.match(/<\/(?:strong|b)>/i)) {
                isBold = false;
            } else if (part) {
                // Behandle BR-Tags
                const textParts = part.split(/<br\s*\/?>/i);
                textParts.forEach((textPart, textIndex) => {
                    if (textPart) {
                        if (isBold) {
                            elements.push(React.createElement('strong', { key: `bold-${index}-${textIndex}` }, textPart));
                        } else {
                            elements.push(textPart);
                        }
                    }
                    // Füge BR nach jedem Teil hinzu (außer dem letzten)
                    if (textIndex < textParts.length - 1) {
                        elements.push(React.createElement('br', { key: `br-${index}-${textIndex}` }));
                    }
                });
            }
        });
        
        return elements;
    };
    
    const containerStyle = {
        ...style,
        // ✅ Stelle sicher, dass alle Font-Properties explizit gesetzt sind
        fontFamily: style.fontFamily || 'inherit',
        fontSize: style.fontSize || 'inherit',
        fontWeight: style.fontWeight || 'inherit',
        color: style.color || 'inherit',
        lineHeight: style.lineHeight || 'inherit',
        // ✅ WHITE-SPACE KONTROLLE: Erlaube Zeilenumbrüche und Leerzeichen
        whiteSpace: style.whiteSpace || 'pre-wrap',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        textAlign: style.textAlign || 'inherit',
        letterSpacing: style.letterSpacing || 'inherit'
    };
    
    return React.createElement('div', {
        style: containerStyle,
        className: className
    }, parseHtmlToReact(content));
};

/**
 * LEGACY: Rendert Rich Text mit dangerouslySetInnerHTML (für Backward Compatibility)
 */
export const renderRichText = (content, style = {}, className = 'rich-text-output') => {
    const cleanedContent = cleanRichTextHTML(content);
    
    return {
        style: {
            ...style,
            fontFamily: style.fontFamily || 'inherit',
            fontSize: style.fontSize || 'inherit',
            fontWeight: style.fontWeight || 'inherit',
            color: style.color || 'inherit',
            lineHeight: style.lineHeight || 'inherit',
            whiteSpace: style.whiteSpace || 'pre-wrap',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            textAlign: style.textAlign || 'inherit',
            letterSpacing: style.letterSpacing || 'inherit'
        },
        className: className,
        dangerouslySetInnerHTML: { __html: cleanedContent }
    };
};

/**
 * Konvertiert Rich Text zu Plain Text (für Fallbacks)
 * @param {string} htmlContent - HTML-String
 * @returns {string} - Plain Text
 */
export const richTextToPlainText = (htmlContent) => {
    if (!htmlContent) return '';
    
    return htmlContent
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .trim();
};

/**
 * Prüft ob Rich Text Content leer ist
 * @param {string} htmlContent - HTML-String
 * @returns {boolean} - True wenn leer
 */
export const isRichTextEmpty = (htmlContent) => {
    if (!htmlContent) return true;
    
    const plainText = richTextToPlainText(htmlContent);
    return plainText.trim().length === 0;
};

/**
 * Erstellt einen Rich Text Container mit optimaler Font-Inheritance
 * @param {string} content - Rich Text Content
 * @param {object} containerStyle - Style für den Container
 * @param {string} fallbackText - Fallback Text wenn Content leer ist
 * @returns {object} - React Component Props
 */
export const createRichTextContainer = (content, containerStyle = {}, fallbackText = '') => {
    if (isRichTextEmpty(content) && !fallbackText) {
        return null;
    }
    
    const displayContent = isRichTextEmpty(content) ? fallbackText : content;
    
    return renderRichText(displayContent, containerStyle);
};
